import React, { useMemo, useState } from "react";
import { MapContainer, Marker, Popup } from "react-leaflet";
import L, { LatLngExpression } from "leaflet";
import "leaflet/dist/leaflet.css";
import classes from "./DataTableHistory.module.css";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import MapBaseLayers, { MAP_MAX_ZOOM } from "../UI/MapBaseLayers";
import { useResponsiveLayout } from "../../hooks/useResponsiveLayout";
interface Props {
  onSelect: (ctx: string) => void;
  selectedContextId?: string;
}

interface MarkerInfo {
  pos: LatLngExpression;
  id: string;
  label: string;
  color: string;
}

const markers: MarkerInfo[] = [
  { pos: [-34.6649, -54.1646], id: "sohma_la_paloma_mareografica_context", label: "SOHMA La Paloma", color: "#6C4AB6" },
  { pos: [-34.865, -54.931], id: "sohma_punta_lobos_mareografica_context", label: "SOHMA Punta Lobos", color: "#8E24AA" },
  { pos: [-34.907, -56.21], id: "anp_muelle_fluvial_mareografo_context", label: "ANP Muelle Fluvial", color: "#0B7285" },
  { pos: [-34.95788, -56.15918], id: "montevideo_boya_context", label: "Boya Montevideo", color: "#1E88E5" },
  { pos: [-34.9442, -55.9322], id: "flowers_island_exo1_context", label: "Isla de Flores EXO1", color: "#C2185B" },
  { pos: [-32.2599361, -52.0779639], id: "simcosta_rs4_context", label: "Simcosta RS-4 (Rio Grande)", color: "#EF476F" },
  { pos: [-34.9145566, -56.1477433], id: "playa_mvd_pocitos_context", label: "Playa Pocitos", color: "#5E60CE" },
  { pos: [-34.9000656, -56.1218391], id: "playa_mvd_buceo_context", label: "Playa Buceo", color: "#4361EE" },
  { pos: [-34.9167086, -56.1697189], id: "playa_mvd_ramirez_context", label: "Playa Ramírez", color: "#4CC9F0" },
  { pos: [-34.8879995, -56.0465748], id: "playa_mvd_carrasco_context", label: "Playa Carrasco", color: "#3A0CA3" },
  { pos: [-34.8978683, -56.1009408], id: "playa_mvd_malvin_context", label: "Playa Malvín", color: "#489F6F" },
  { pos: [-34.8521877, -56.0542171], id: "playa_mvd_arroyo_carrasco_context", label: "Arroyo Carrasco", color: "#2D6A4F" },
  { pos: [-34.8869938, -56.0903251], id: "playa_mvd_arroyo_molino_context", label: "Arroyo Molino", color: "#40916C" },
  { pos: [-34.8958501, -56.0849209], id: "playa_mvd_de_los_ingleses_context", label: "Playa de los Ingleses", color: "#74C69D" },
  { pos: [-34.8991968, -56.0679555], id: "playa_mvd_de_la_mulata_context", label: "Playa de la Mulata", color: "#95D5B2" },
  { pos: [-34.898384, -56.0727453], id: "playa_mvd_verde_context", label: "Playa Verde", color: "#B5E48C" },
  { pos: [-34.8961014, -56.0939784], id: "playa_mvd_honda_context", label: "Playa Honda", color: "#D9ED92" },
  { pos: [-34.8658027, -56.0212387], id: "playa_mvd_miramar_context", label: "Playa Miramar", color: "#F77F00" },
  { pos: [-34.9072, -56.0815], id: "playa_mvd_brava_context", label: "Playa Brava", color: "#F48C06" },
  { pos: [-34.8876638, -56.3310749], id: "playa_mvd_de_los_cilindros_context", label: "Playa de los Cilindros", color: "#FAA307" },
  { pos: [-34.8702782, -56.3401352], id: "playa_mvd_pajas_blancas_context", label: "Playa Pajas Blancas", color: "#FFB703" },
  { pos: [-34.894595, -56.3044786], id: "playa_mvd_punta_yeguas_context", label: "Playa Punta Yeguas", color: "#E85D04" },
  { pos: [-34.894526, -56.2909791], id: "playa_mvd_santa_catalina_context", label: "Playa Santa Catalina", color: "#DC2F02" },
  { pos: [-34.8584336, -56.3757289], id: "playa_mvd_la_colorada_context", label: "Playa La Colorada", color: "#9D0208" },
  { pos: [-34.8267787, -56.3875821], id: "playa_mvd_punta_espinillo_context", label: "Playa Punta Espinillo", color: "#6A040F" },
  { pos: [-34.8741473, -56.3357251], id: "playa_mvd_zabala_context", label: "Playa Zabala", color: "#7209B7" },
  { pos: [-34.8961939, -56.2524091], id: "playa_mvd_del_cerro_context", label: "Playa del Cerro", color: "#560BAD" },
  { pos: [-34.7365234, -56.4530498], id: "playa_mvd_del_nacional_o_frigonal_context", label: "Playa del Nacional (Frigonal)", color: "#277DA1" },
  { pos: [-34.4720, -57.8430], id: "anp_colonia_mareografo_context", label: "ANP Colonia", color: "#0D9488" },
  { pos: [-33.1160, -58.2270], id: "anp_fray_bentos_mareografo_context", label: "ANP Fray Bentos", color: "#0F766E" },
  // Leve desplazamiento frente al EXO1 para que se vean separados en la isla
  { pos: [-34.9442, -55.92], id: "anp_isla_de_flores_mareografo_context", label: "ANP Isla de Flores", color: "#1D4ED8" },
  { pos: [-34.6649, -54.1646], id: "anp_la_paloma_mareografo_context", label: "ANP La Paloma", color: "#2563EB" },
  { pos: [-33.8790, -58.4060], id: "anp_nueva_palmira_mareografo_context", label: "ANP Nueva Palmira", color: "#0891B2" },
  { pos: [-32.3130, -58.0780], id: "anp_paysandu_mareografo_context", label: "ANP Paysandú", color: "#06B6D4" },
  { pos: [-34.190000, -58.256944], id: "shn_martin_garcia_mareografica_context", label: "SHN Martín García", color: "#1B5E20" },
  { pos: [-34.626250, -57.927417], id: "shn_pilote_norden_mareografica_context", label: "SHN Pilote Norden", color: "#2E7D32" },
  { pos: [-35.100278, -57.127500], id: "shn_oyarvide_mareografica_context", label: "SHN Oyarvide", color: "#388E3C" },
  { pos: [-34.560833, -58.398889], id: "shn_buenos_aires_mareografica_context", label: "SHN Buenos Aires", color: "#43A047" },
  { pos: [-34.833889, -57.880278], id: "shn_la_plata_mareografica_context", label: "SHN La Plata", color: "#4CAF50" },
  { pos: [-35.015278, -57.536111], id: "shn_atalaya_mareografica_context", label: "SHN Atalaya", color: "#66BB6A" },
  { pos: [-34.433889, -58.540278], id: "shn_san_fernando_mareografica_context", label: "SHN San Fernando", color: "#81C784" },
  { pos: [-36.354722, -56.715000], id: "shn_san_clemente_mareografica_context", label: "SHN San Clemente del Tuyú", color: "#A5D6A7" },
];

const buildMarkerIcon = (color: string, isSelected: boolean) => {
  const size = isSelected ? 20 : 16;
  const border = isSelected ? 3 : 2;
  return L.divIcon({
    className: "historical-context-marker",
    html: `<span style="display:inline-block;width:${size}px;height:${size}px;border-radius:50%;background:${color};border:${border}px solid #fff;box-shadow:0 0 4px rgba(0,0,0,0.25);"></span>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
};

export const MapComponent: React.FC<Props> = ({ onSelect, selectedContextId }) => {
  const position: LatLngExpression = [-34.92, -56.0];
  const [searchTerm, setSearchTerm] = useState("");
  const mobile = useResponsiveLayout() === "mobile";

  const filteredMarkers = useMemo(
    () =>
      markers.filter((m) =>
        m.label.toLowerCase().includes(searchTerm.trim().toLowerCase())
      ),
    [searchTerm]
  );

  return (
    <div className={classes.card}>
      <div className={classes.card_content}>
        <div style={{ width: "100%", padding: "20px 18px 8px 18px" }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Filtrar estación (ej: ANP, isla, playa...)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </div>
        <div style={{ width: "100%" }}>
          <MapContainer
            className="proteo-map"
            attributionControl={false}
            zoomControl={false}
            center={position}
            zoom={6.8}
            maxZoom={MAP_MAX_ZOOM}
            style={{ height: "600px", width: "100%" }}
          >
            <MapBaseLayers />

            {filteredMarkers.map((m) => {
              const icon = buildMarkerIcon(m.color, m.id === selectedContextId);
              return (
                <Marker
                  key={m.id}
                  position={m.pos}
                  icon={icon}
                  eventHandlers={{ click: () => onSelect(m.id) }}
                >
                  {!mobile && <Popup>{m.label}</Popup>}
                </Marker>
              );
            })}
          </MapContainer>
        </div>
      </div>
    </div>
  );
};
